import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ContentComponent } from 'Components/ContentComponent/ContentComponent';
import {
    getPlayerPromotionsData,
    OLDgetPlayerReport,
} from 'redux/actions/backoffice/player-report-actions';
import { getGameHistoryReport } from 'redux/actions/backoffice/game-history-actions';
import { currencyUtils, dateFormatter, localStorageHelper, Messages } from 'utils';
import { IPlayerData } from 'redux/reducers/backoffice/player-report-reducer';
import { HeaderTitleComponent } from 'Components/ContentComponent/HeaderTitleComponent';
import { Button, Checkbox, Divider, Empty, Input, Modal, Popover, Radio, Space, Table } from 'antd';
import { Loader } from 'Components/Loader/Loader';
import { Context } from 'App';
import { ErrorComponent } from 'Containers/Errors/ErrorComponent';
import {
    getPlayer,
    getPlayerCampaigns,
    getPlayerCurrencyId,
    getPlayerInternalId,
    getPlayerRawData,
    playerDoesntExist,
} from 'redux/selectors/backoffice/player-report-selectors';
import { getGameHistory } from 'redux/selectors/backoffice/game-history-selectors';
import { getServerError } from 'redux/selectors/backoffice/error-selectors';
import { Link, useParams } from 'react-router-dom';
import { PlayerReportColumns } from './PlayerProfileColumns';
import { SettingOutlined } from '@ant-design/icons';
import {
    getAreGameConfigurationsLoading,
    getDateOfLastRefresh,
} from '../../../redux/selectors/user-selectors';
import GameHistory from '../PlayersReport/GameHistory';
import OPAGameHistory from '../../Reports/PlayersReport/OPAGameHistory';
import { getAllGameConfigurationsByUser } from '../../../redux/actions/user-actions';
import OldTableHeaderWrapper from '../../../Components/Table/(Old)TableHeaderWrapper';
import { PageNotFoundComponent } from 'Containers/Errors/PageNotFoundComponent';
import { canAccessPlayerProfile } from 'utils';

const { Search } = Input;

const OLDPlayerProfileComponent: React.FC = () => {
    const dispatch = useDispatch();
    const { login, playerId } = useParams();
    const { t } = useTranslation();
    const playerData = useSelector(getPlayer);
    const noPlayer = useSelector(playerDoesntExist);
    const playerInternalId = useSelector(getPlayerInternalId);
    const playerRawData = useSelector(getPlayerRawData);
    const campaigns = useSelector(getPlayerCampaigns);
    const currencyId = useSelector(getPlayerCurrencyId);
    const currencyCode = currencyUtils.getCurrencyDataById(currencyId)?.code;
    const gameHistory = useSelector(getGameHistory);
    const areGameConfigurationsLoading = useSelector(getAreGameConfigurationsLoading);
    const error = useSelector(getServerError);
    const dateOfLastRefresh: Date = useSelector(getDateOfLastRefresh);
    const shouldDisplayFreeRounds = localStorageHelper
        .getChainedValue('user.authorities')
        .includes('VIEW_CAMPAIGNS');
    const tableRef: Parameters<typeof Table>[0]['ref'] = React.useRef(null);

    const getGameHistoryData = useCallback(
        (playerData: IPlayerData) => {
            const { playerId, gameSessionId, startId, gameType } = playerData;
            setChosenPlayer(playerData);

            if (['SLOT', 'PMR', 'FAST_GAME'].includes(gameType)) {
                dispatch(getGameHistoryReport({ playerId, gameSessionId, startId }));
            }
        },
        [dispatch],
    );

    const columns = PlayerReportColumns(
        currencyCode,
        getGameHistoryData,
        playerInternalId,
        playerRawData,
        login,
        areGameConfigurationsLoading,
    );
    const options = columns.map(({ key, label }) => ({
        label: t(label),
        value: key,
    }));
    const [tableSize, setTableSize] = useState<'small' | 'middle' | 'large'>('middle');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [tableHeight, setTableHeight] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [visibleData, setVisibleData] = useState<any[]>([]);
    const [highlightKey, setHighlightKey] = useState<any>(null);
    const [checkedList, setCheckedList] = useState(columns.map((item) => item.key));
    const initialDates = localStorageHelper.getChainedValue('user.reports.dates');
    const [fromDate, setFromDate] = useState<Date>(
        initialDates?.fromDate || dateFormatter.getDefaultDate(),
    );
    const [toDate, setToDate] = useState<Date>(
        initialDates?.toDate || dateFormatter.getDefaultDate(false),
    );
    const [chosenPlayer, setChosenPlayer] = useState<IPlayerData | null>(null);
    const visibleColumns = columns.filter((column) => checkedList.includes(column.key));
    const totalWidth = visibleColumns.reduce((total, col) => total + (col.width || 0), 0);

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight * 0.8 - 194;
        setTableHeight(height);
    }, []);

    const toggleModal = () => {
        setIsModalVisible(!isModalVisible);
    };

    const getReport = useCallback(() => {
        shouldDisplayFreeRounds && dispatch(getPlayerPromotionsData({ fromDate, toDate, login }));

        if (playerId === 'null') {
            dispatch(OLDgetPlayerReport({ fromDate, toDate, login }));
        } else {
            dispatch(OLDgetPlayerReport({ fromDate, toDate, playerId }));
        }
    }, [fromDate, toDate, playerId, login, dispatch, shouldDisplayFreeRounds]);

    const parentReportCallback = useCallback(
        (dates: { fromDate: Date; toDate: Date }) => {
            const { fromDate, toDate } = dates;

            localStorageHelper.setChainedValue(`user.reports.dates`, {
                fromDate,
                toDate,
            });

            setFromDate(fromDate);
            setToDate(toDate);
            getReport();
        },
        [getReport],
    );

    const handlePageSizeChange = (current: any, size: any) => {
        setPageSize(size);
    };

    const handleTableChange = (extra: any) => {
        if (extra && extra.currentDataSource) {
            setVisibleData(extra.currentDataSource);
        }
    }

    const handleSearchByRoundId = (value: any) => {
        const index = visibleData.findIndex((item) =>  item.roundId === value);
        if (index === -1) {
            Messages.error(t('round_not_found'));
            return;
        }
        const targetPage = Math.floor(index / pageSize) + 1;
        setCurrentPage(targetPage);
        setTimeout(() => {
            tableRef.current?.scrollTo({key: value})
            setHighlightKey(value);
        }, 100)
    }

    const closeGameHistory = useCallback(() => {
        setChosenPlayer(null);
    }, []);

    const tryToOpenGameHistoryFromURLParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const gameSessionId = searchParams.get('gameSessionId');
        const playerId = searchParams.get('playerId') || playerInternalId;
        const startId = searchParams.get('startId');

        if (gameSessionId && playerId && startId) {
            const chosenPlayer = playerData.find((data: any) => data.startId === Number(startId));

            if (chosenPlayer) {
                getGameHistoryData({ ...chosenPlayer, playerId: Number(playerId) });
            }
        }
    }, [playerData, getGameHistoryData, playerInternalId]);

    const chooseGameHistory = useCallback(() => {
        if (!gameHistory || !gameHistory.length || !chosenPlayer) return null;

        return ['SLOT', 'PMR', 'FAST_GAME'].includes(chosenPlayer.gameType) ? (
            <GameHistory
                data={gameHistory}
                summaryData={chosenPlayer}
                currencyId={currencyId}
                onClose={closeGameHistory}
                t={t}
                error={error}
                player={chosenPlayer}
            />
        ) : chosenPlayer.gameType === 'MINES' ? (
            <OPAGameHistory
                roundId={chosenPlayer?.roundId}
                onClose={closeGameHistory}
                t={t}
                error={error}
            />
        ) : null;
    }, [gameHistory, chosenPlayer, currencyId, t, error, closeGameHistory]);

    const reportData = playerData ? (playerData.length ? playerData : []) : null;

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    useEffect(() => {
        if (playerData) {
            tryToOpenGameHistoryFromURLParams();
            setVisibleData((playerData.length ? playerData : []))
        }
    }, [playerData, tryToOpenGameHistoryFromURLParams]);

    useEffect(() => {
        getReport();
    }, [getReport]);

    useEffect(() => {
        dispatch(getAllGameConfigurationsByUser());
    }, [dispatch]);

    if (!canAccessPlayerProfile()) {
        return <PageNotFoundComponent />;
    }

    return (
        <ContentComponent
            header={
                <Context.Consumer>
                    {(context) =>
                        noPlayer ? (
                            <HeaderTitleComponent
                                title={login}
                                customBreadcrumbs={<div>{t('player')}</div>}
                            />
                        ) : (
                            <OldTableHeaderWrapper
                                titleComponent={
                                    <HeaderTitleComponent
                                        title={login}
                                        customBreadcrumbs={<div>{t('player')}</div>}
                                    />
                                }
                                getReport={getReport}
                                parentReportCallback={parentReportCallback}
                                fromDate={fromDate}
                                toDate={toDate}
                                login={login}
                                playerId={playerId}
                                context={context}
                                reportKey={'playerProfile'}
                                notShowFilters
                                dateOfLastRefresh={dateOfLastRefresh}
                            />
                        )
                    }
                </Context.Consumer>
            }
            preHeader={
                <div style={{ marginBottom: '8px' }}>
                    {(campaigns && campaigns?.awardedFeature.campaigns.length > 0) ||
                    (campaigns && campaigns?.freeRounds.campaigns.length > 0) ? (
                        <Popover
                            placement="right"
                            content={
                                <>
                                    {campaigns.freeRounds.campaigns.length > 0 && (
                                        <label className="font-bold">Free Rounds:</label>
                                    )}
                                    <ol>
                                        {campaigns.freeRounds.campaigns.map((campaign: any) => (
                                            <li key={campaign.campaignId}>
                                                <Link
                                                    to={`/casino/promotions/${campaign.campaignId}`}
                                                >
                                                    {`${t('promotionId')} ${campaign.campaignId} - (${campaign.playedRounds}/${campaign.assignedRounds})`}
                                                </Link>
                                            </li>
                                        ))}
                                    </ol>
                                    {campaigns.awardedFeature.campaigns.length > 0 && (
                                        <label className="font-bold">Awarded Feature:</label>
                                    )}
                                    <ol>
                                        {campaigns.awardedFeature.campaigns.map((campaign: any) => (
                                            <li key={campaign.campaignId}>
                                                <Link
                                                    to={`/casino/awarded-promotion/${campaign.campaignId}`}
                                                >
                                                    {`${t('promotionId')} ${campaign.campaignId} - (${campaign.playedRounds}/${campaign.assignedRounds})`}
                                                </Link>
                                            </li>
                                        ))}
                                    </ol>
                                </>
                            }
                        >
                            <Button>
                                {`Promotion Rounds ${campaigns.awardedFeature.playedRounds + campaigns.freeRounds.playedRounds}/${campaigns.awardedFeature.assignedRounds + campaigns.freeRounds.assignedRounds}`}
                            </Button>
                        </Popover>
                    ) : null}
                </div>
            }
            innerContent={
                <>
                    {error ? (
                        <ErrorComponent error={error} />
                    ) : reportData ? (
                        reportData.length ? (
                            <div className="casino_performance-table-container">
                                <div className={'casino_performance-table-title'}>
                                    <div className="content-wrapper">
                                        <Space>
                                            <Button
                                                style={{ marginRight: '8px', zIndex: '1' }}
                                                onClick={toggleModal}
                                                icon={<SettingOutlined />}
                                            />
                                            <Search
                                                className="z-[1] relative w-96"
                                                placeholder={ t('enter_search_value')}
                                                onSearch={handleSearchByRoundId}
                                                enterButton
                                            />
                                        </Space>
                                    </div>
                                    <Modal
                                        title="Table tools"
                                        open={isModalVisible}
                                        onOk={toggleModal}
                                        onCancel={toggleModal}
                                        footer={[
                                            <Button
                                                key="submit"
                                                type="primary"
                                                onClick={toggleModal}
                                            >
                                                OK
                                            </Button>,
                                        ]}
                                    >
                                        <Divider>Columns displayed</Divider>
                                        <Checkbox.Group
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                marginBottom: '20px',
                                            }}
                                            options={options}
                                            value={checkedList}
                                            onChange={(checkedValues) => {
                                                setCheckedList(checkedValues.map(String));
                                            }}
                                        />
                                        <Divider>Columns displayed</Divider>
                                        <div style={{ marginBottom: 16 }}>
                                            <span>Change Table Cell Size: </span>
                                            <Radio.Group
                                                value={tableSize}
                                                onChange={(e) => setTableSize(e.target.value)}
                                            >
                                                <Radio.Button value="large">Large</Radio.Button>
                                                <Radio.Button value="middle">Middle</Radio.Button>
                                                <Radio.Button value="small">Small</Radio.Button>
                                            </Radio.Group>
                                        </div>
                                    </Modal>
                                </div>
                                <Table
                                    style={{
                                        minWidth: '100%',
                                        paddingBottom: '16px',
                                    }}
                                    ref={tableRef}
                                    className={'casino_performance-table'}
                                    dataSource={reportData}
                                    scroll={{ x: totalWidth, y: tableHeight }}
                                    columns={visibleColumns}
                                    pagination={{
                                        pageSize,
                                        current: currentPage,
                                        onChange: (page) => setCurrentPage(page),
                                        position: ['topRight'],
                                        pageSizeOptions: [10, 20, 50, 100, 500, 1000],
                                        onShowSizeChange: handlePageSizeChange,
                                    }}
                                    onChange={(_pagination, _filters, _sorter, extra) => {
                                        handleTableChange(extra);
                                    }}
                                    size={tableSize}
                                    rowKey={(record) => `${record.roundId.toString()}`}
                                    rowClassName={(record) =>
                                       record.roundId === highlightKey ? "animate-flash" : ""
                                    }
                                    footer={() => <></>}
                                />
                            </div>
                        ) : (
                            <Empty description={t('no_data')} />
                        )
                    ) : noPlayer ? (
                        <Empty description={t('player_is_not_yet_exists')} />
                    ) : (
                        <Loader style={{ height: '90vh' }} />
                    )}
                    {chooseGameHistory()}
                </>
            }
            reportKey={'playerProfile'}
        />
    );
};

export default OLDPlayerProfileComponent;
